import {
  Button,
  Center,
  Input,
  Paper,
  ScrollArea,
  SimpleGrid,
  Skeleton,
  Stack,
  Text
} from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { IconApps } from '@tabler/icons-react'
import { FormattedMessage } from 'react-intl'
import { Link } from 'react-router'
import { SelectableCard } from '@/components/ui-shared/SelectableCard/SelectableCard'
import { useGetDeploymentList } from '@/queries/deploymentQueries'
import { RouterPath } from '@/router/paths'
import { Deployment, InputSourceType } from '@/types/deployment'
import { MAX_ITEMS_WITHOUT_PAGINATION } from '@/utils/pagination'
import { PipelineFormValues } from './VlmPipelineForm'

type DeploymentsByApp = Record<
  string,
  { application_name: string; deployments: Deployment[] }
>

type DeploymentListContentProps = {
  grouped: DeploymentsByApp
  form: UseFormReturnType<PipelineFormValues>
}

const DeploymentListContent = ({
  grouped,
  form
}: DeploymentListContentProps) => (
  <ScrollArea h={320}>
    <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} p="sm">
      {Object.entries(grouped).map(
        ([appId, { application_name, deployments }]) => (
          <SelectableCard
            key={appId}
            height={140}
            isSelected={form.values.source.applicationId === appId}
            onClick={() => form.setFieldValue('source.applicationId', appId)}
          >
            <Stack gap={4} align="center" justify="center" h="100%">
              <IconApps size={24} color="var(--mantine-color-teal-6)" />

              <Text size="sm" fw={600} ta="center" lineClamp={2}>
                {application_name}
              </Text>

              <Text size="xs" c="dimmed">
                <FormattedMessage
                  id="vlm.pipelines.create.visionplatform.cameraCount"
                  values={{ count: deployments.length }}
                />
              </Text>
            </Stack>
          </SelectableCard>
        )
      )}
    </SimpleGrid>
  </ScrollArea>
)

const EmptyContent = () => (
  <Stack gap="sm" align="center" p="xl">
    <Text size="sm" c="dimmed" ta="center">
      <FormattedMessage id="vlm.pipelines.create.visionplatform.empty" />
    </Text>

    <Button
      component={Link}
      to={RouterPath.deployments}
      size="xs"
      variant="outline"
    >
      <FormattedMessage id="vlm.pipelines.create.visionplatform.empty.link" />
    </Button>
  </Stack>
)

type VpApplicationSourceSettingsProps = {
  form: UseFormReturnType<PipelineFormValues>
}

export const VpApplicationSourceSettings = ({
  form
}: VpApplicationSourceSettingsProps) => {
  const deviceId = form.values.deviceId
  const { data, isLoading, isError } = useGetDeploymentList(
    { limit: MAX_ITEMS_WITHOUT_PAGINATION, device_id: deviceId },
    { enabled: !!deviceId }
  )

  // TODO: replace with a backend filter (e.g. input_source_type=cameras) when the API supports it
  const cameraDeployments =
    data?.results.filter(
      (d) => d.settings_payload?.input_source_type === InputSourceType.Cameras
    ) ?? []

  const grouped: DeploymentsByApp = cameraDeployments.reduce<DeploymentsByApp>(
    (acc, deployment) => {
      const { application_id, application_name } = deployment

      if (!acc[application_id]) {
        acc[application_id] = { application_name, deployments: [] }
      }

      acc[application_id].deployments.push(deployment)
      return acc
    },
    {}
  )

  const isEmpty = !isLoading && !isError && Object.keys(grouped).length === 0

  const renderContent = () => {
    if (isLoading)
      return (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} p="sm">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} height={140} radius="md" />
          ))}
        </SimpleGrid>
      )

    if (isError)
      return (
        <Center p="xl">
          <Text size="sm" c="red">
            <FormattedMessage id="general.list.error" />
          </Text>
        </Center>
      )

    if (isEmpty) return <EmptyContent />

    return <DeploymentListContent grouped={grouped} form={form} />
  }

  return (
    <Input.Wrapper
      label={<FormattedMessage id="apps.myApps" />}
      error={form.errors['source.applicationId']}
    >
      <Paper my={4} withBorder>
        {renderContent()}
      </Paper>
    </Input.Wrapper>
  )
}
